// ════════════════════════════════════════════════════════════════════════════
//  ManDana — one door, and everything heavy goes through it
//  ---------------------------------------------------------------------------
//  Before this file, four modules kept bytes and none of them agreed.
//  md-file.ts opens and closes a connection per call and stores Blobs;
//  md-mstore.ts stores JSON strings; md-boarddb.js stores objects;
//  md-person.ts skips a local cache entirely and talks straight to the
//  transport. Every one of them is defensible alone. Together they are four
//  places that each have to independently remember to verify a download, four
//  places that each have to be taught about an account compartment, and four
//  places a chunked upload would have to be written into.
//
//  So: one door. A caller says "keep these bytes" and "give me the bytes named
//  by this hash". The local cache, the transport, the verification and the
//  ledger of what the store has not taken yet are this module's business and
//  nobody else's.
//
//  ── THE NAME IS THE CHECKSUM, SO THE CHECKSUM IS CHECKED ───────────────────
//  Content addressing is only worth something if somebody verifies it. A
//  proxy, a half-read from a bucket, a Blossom server run by a stranger — any
//  of them can hand back the wrong bytes, and if those are cached under the
//  hash we asked for, this device is now wrong about that file for ever and
//  has no way to find out. Every download is hashed before it is believed, and
//  a mismatch is refused AND NOT KEPT, so the next attempt can still succeed.
//
//  That check is also the thing that makes reading from somebody else's server
//  safe at all, which is why it is here and not in the transport: a transport
//  is a stranger, and this is the line that does not trust one.
//
//  ── THE CACHE COMES FIRST, THE STORE CATCHES UP ────────────────────────────
//  A put that went to the network first would lose the file whenever the
//  network was down, which is most of the reason to have a local copy. So the
//  bytes land here, the hash goes on the PENDING list, and the store is asked
//  after. A refusal is returned by name and the bytes stay readable on the
//  device that made them. Story 1.6 makes that pending list durable across a
//  reload; here it only has to exist and be honest about what is owed.
//
//  ── AND THE TRIM MAY NEVER TOUCH THEM ──────────────────────────────────────
//  Story 0.9 stated the rule and this is where it is enforced: the only copy
//  of a blob the store has not taken is the one in this cache. Evicting it is
//  not cache management, it is the user's file gone. The trim goes over its
//  target instead, and says how much it could not free.
//
//  ── WHAT IS NOT HERE ───────────────────────────────────────────────────────
//  No URL, no status code, no transport's name. This module opens whatever
//  mdTransportName() answers. tests/blob-door.test.js reads this file to prove
//  it, and drives every path above through a fake store with no network under
//  it — which is the seam's whole promise, exercised rather than asserted.
//
//  It also does not RELEASE anything on its own. Phase 1 ships the
//  conservative rule: only account deletion reclaims bytes. mdBlobRelease
//  exists and works; what turns it on at record deletion is story 2.2, once
//  0.7's per-record references have been proven under two-device use. Costs
//  some unreclaimed storage, cannot lose a file — the right way round for a
//  failure that would otherwise be silent.
// ════════════════════════════════════════════════════════════════════════════
(function (g: any) {
  const DB_NAME = 'mandana-blobs';
  /* 2, because the queue store was added after 1 shipped on this branch. A new
     object store at the old number never runs onupgradeneeded on a device that
     already opened the database, so every queue write throws NotFoundError
     into a catch that swallows it — and the swallowing is right, an unreadable
     queue is a lost retry rather than a lost file, which is exactly why this
     would have been silent. Bump the number whenever a store is added. */
  const DB_VERSION = 2;
  /* TWO STORES, ON PURPOSE. Summing the cache and choosing what to evict must
     never deserialise the bytes — a device holding half a gigabyte would read
     all of it into memory to answer "how much am I using?", which is a
     question the storage widget asks on every visit to Settings. So the sizes
     live apart from the things they measure. */
  const BYTES = 'bytes';
  const META = 'meta';
  /* And what is still owed to the store. It is HERE rather than in
     localStorage — where the plan first put it, by analogy with
     md-storage.js's engine-lag ledger — for three reasons, in increasing
     order of how much they matter.

     Third: this module is not allowed to touch localStorage at all, and
     tests/storage.test.js:170-193 fails any module but md-storage.js that
     does. Second: a few thousand chunk hashes is a real fraction of the ~5 MB
     wall, which is the exact ceiling this plan exists to relieve. First, and
     the one that would have bitten: a queue in one store naming bytes in
     another can disagree with itself, and an entry whose bytes the cache lost
     retries for ever while reporting an upload in progress. Here they are one
     database and they cannot drift. */
  const QUEUE = 'queue';

  const HASH = /^[0-9a-f]{64}$/;
  const isHash = (value: unknown): value is string =>
    typeof value === 'string' && HASH.test(value);

  /* Resolved once and held, exactly as md-versions.ts and md-boarddb.js do: a
     database name that changed mid-session is a device losing track of its own
     files halfway through reading them. Asked defensively because md-adopt.ts
     loads first in the app but not in every harness, and the bare name is the
     identity compartment's own answer. */
  let dbName: string | null = null;
  function nameOf(): string {
    if (dbName !== null) return dbName;
    const resolved: string = (typeof g.mdCompartmentDbName === 'function')
      ? String(g.mdCompartmentDbName(DB_NAME) || DB_NAME) : DB_NAME;
    dbName = resolved;
    return resolved;
  }

  /* ── the cache ─────────────────────────────────────────────────────────── */

  let dbPromise: Promise<any> | null = null;

  function open(): Promise<any> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
      if (!g.indexedDB) { dbPromise = null; reject(new Error('no indexeddb')); return; }
      const req = g.indexedDB.open(nameOf(), DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(BYTES)) db.createObjectStore(BYTES);
        if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
        if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE);
      };
      req.onsuccess = () => {
        const db = req.result;
        /* Both drops check the cache is still theirs: a close arriving after
           a later call has already opened a live connection must not throw
           that one away. */
        const mine = dbPromise;
        db.onversionchange = () => {
          try { db.close(); } catch (e) { /* already closing */ }
          if (dbPromise === mine) dbPromise = null;
        };
        /* The browser closed it — a suspended tab, memory reclaimed. Nothing
           failed yet; everything would, for ever, on a handle kept past this. */
        db.onclose = () => { if (dbPromise === mine) dbPromise = null; };
        resolve(db);
      };
      req.onerror = () => {
        dbPromise = null;
        reject(req.error || new Error('indexeddb open failed'));
      };
      req.onblocked = () => { dbPromise = null; reject(new Error('indexeddb blocked')); };
    });
    return dbPromise;
  }

  function wrap<T>(req: any): Promise<T> {
    return new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('indexeddb request failed'));
    });
  }

  function shelf(db: any, name: string, mode: string) {
    try {
      return db.transaction(name, mode).objectStore(name);
    } catch (err: any) {
      /* InvalidStateError here means the connection is finished and the
         database is untouched — WebKit does not always send onclose, so this
         is the other half of noticing. Drop it and let this call fail
         honestly; the next one opens a live handle rather than the corpse. */
      if (err && err.name === 'InvalidStateError' && dbPromise) dbPromise = null;
      throw err;
    }
  }

  /* Every row of the meta store. getAll where a browser has it, and the slow
     way where it does not — the fake in tests/helpers only implements the keys
     half, and a real IndexedDB is not the only thing this has to run on. */
  async function allRows(db: any, name: string): Promise<Array<[string, any]>> {
    const rows: Array<[string, any]> = [];
    const box = shelf(db, name, 'readonly');
    if (typeof box.getAll === 'function' && typeof box.getAllKeys === 'function') {
      const keys = await wrap<string[]>(box.getAllKeys());
      const values = await wrap<any[]>(shelf(db, name, 'readonly').getAll());
      for (let i = 0; i < keys.length; i += 1) rows.push([keys[i] as string, values[i]]);
      return rows;
    }
    const keys = await wrap<string[]>(box.getAllKeys());
    for (const key of keys) {
      const value = await wrap<any>(shelf(db, name, 'readonly').get(key));
      if (value) rows.push([key, value]);
    }
    return rows;
  }

  /* Last-touched, and a counter beside it. Date.now() alone cannot order three
     writes inside one millisecond, which is exactly what a page doing its
     opening reads looks like — and getting that order wrong evicts the blob
     somebody is about to look at. Across a reload the clock dominates, which
     is the right way round. */
  let seq = 0;
  const stamp = () => ({ at: Date.now(), seq: (seq += 1) });
  const olderFirst = (a: any, b: any) =>
    (a.at - b.at) || ((a.seq || 0) - (b.seq || 0));

  async function keep(hash: string, bytes: Uint8Array, type: string): Promise<boolean> {
    try {
      const db = await open();
      await wrap(shelf(db, BYTES, 'readwrite').put(bytes, hash));
      await wrap(shelf(db, META, 'readwrite').put(
        { size: bytes.length, type: type || '', ...stamp() }, hash,
      ));
      return true;
    } catch (err) {
      /* A cache that cannot write is a slow app, not a broken one — the store
         is still the source of truth. The put below still happens. */
      return false;
    }
  }

  async function touch(hash: string): Promise<void> {
    try {
      const db = await open();
      const meta = await wrap<any>(shelf(db, META, 'readonly').get(hash));
      if (!meta) return;
      await wrap(shelf(db, META, 'readwrite').put({ ...meta, ...stamp() }, hash));
    } catch (err) { /* an unrecorded read costs eviction order, nothing more */ }
  }

  async function drop(hash: string): Promise<void> {
    try {
      const db = await open();
      await wrap(shelf(db, BYTES, 'readwrite').delete(hash));
      await wrap(shelf(db, META, 'readwrite').delete(hash));
    } catch (err) { /* it stays; the next trim will find it again */ }
  }

  /* ── what the store has not taken ──────────────────────────────────────── */

  /* Two kinds of not-yet, and they are not the same thing.

       OWED    `unreachable` — the network. Retry, quietly, for ever.
       FAILED  too_large, over_allowance, unauthorised — retrying is a loop.

     A failed entry stops being sent and starts being something to TELL
     somebody: 1.17's "Failed (over_allowance)", which offers cancel rather
     than retry. Its bytes stay immune to the trim exactly as an owed one's do,
     because they are still the only copy in existence and the store saying no
     once is not a reason to delete somebody's file.

     An UNKNOWN reason is treated as terminal. Guessing retryable turns a word
     nobody has seen before into an infinite loop against somebody's store;
     guessing terminal shows them a refusal they can act on. Only one of those
     is recoverable by a person. */
  const queue = new Map<string, any>();

  /* CONTENT DEDUPES; OWNERSHIP MUST NOT. One set of bytes may be owned by
     several records at once — the same image dropped on two desks is one
     object with two holds, and that is the whole point of addressing bytes by
     their content. So the row is keyed by hash and carries a LIST of owners.

     A row holding a single `ref` looked right and lost data: the second
     record's put overwrote the first's row, the drain placed one hold, and
     releasing that record collected an object the other one still named. */
  const refsOf = (row: any): string[] =>
    (row && Array.isArray(row.refs) ? row.refs.filter((r: any) => typeof r === 'string' && r) : []);

  const isOwed = (reason: string): boolean =>
    Array.isArray(g.MD_BLOB_RETRYABLE) && g.MD_BLOB_RETRYABLE.includes(reason);

  /** Every hash the store has not taken — owed and failed alike. Read by the
      trim, which may evict none of them, and by md-sync.js's pending keys, so
      a document naming one is not pushed before its bytes exist anywhere but
      here. Synchronous because both callers are on hot paths. */
  function mdBlobPending(): string[] { return [...queue.keys()]; }

  /** The DOCUMENTS that own bytes still in flight — never the hashes. This is
      what md-sync.js's mdPendingKeys asks for, and the difference between the
      two is the whole point: a hash means nothing to the sync layer, which
      speaks in keys as mdLocalDocs names them (`board:abc123`). A document
      pushed while its bytes are still only here is the unresolvable reference
      story 0.9 exists to prevent. */
  function mdBlobPendingRefs(): string[] {
    const out = new Set<string>();
    for (const row of queue.values()) for (const ref of refsOf(row)) out.add(ref);
    return [...out];
  }

  /** The ones a person has to decide about, with the word that explains why. */
  function mdBlobFailed(): Array<{ hash: string; reason: string }> {
    const out = [];
    for (const [hash, row] of queue) {
      if (row.state === 'failed') out.push({ hash, reason: row.reason });
    }
    return out;
  }

  /** Everything owed, oldest first — what 1.17 draws and 5.5 lists. Read from
      the database rather than from memory, so what it reports is what would
      survive the tab closing. */
  async function mdBlobQueue(): Promise<any[]> {
    try {
      const db = await open();
      const rows = (await allRows(db, QUEUE)).map(([, row]) => row).filter(Boolean);
      rows.sort(olderFirst);
      return rows;
    } catch (err) { return []; }
  }

  /* READ BACK WITHOUT BEING ASKED. A durable queue is only worth writing if
     something reads it, and this module has no boot hook to be added to that
     would not teach md-app.jsx that blobs have a queue at all. So it reads
     itself, once, at load — and the two things that would be wrong to do
     against a map that has not been filled in yet wait for that read: the
     drain, which would send nothing and report the pass done, and the trim,
     which would see no owner for bytes that are the only copy and free them.

     Memoised rather than awaited at load, because a module body cannot await
     and a rejected floating promise is an unhandled rejection. mdBlobQueueLoad
     stays exported: a test that wants the boot deterministic rather than raced
     awaits it, and awaiting it twice is free. */
  let hydrating: Promise<void> | null = null;
  const hydrated = (): Promise<void> => (hydrating || (hydrating = mdBlobQueueLoad()));

  /** Read the queue back into memory. Called once at load, and by tests that
      want the boot deterministic rather than raced. */
  async function mdBlobQueueLoad(): Promise<void> {
    try {
      const db = await open();
      for (const [hash, row] of await allRows(db, QUEUE)) {
        if (isHash(hash) && row) queue.set(hash, row);
      }
    } catch (err) { /* an unreadable queue is a lost retry, not a lost file */ }
  }

  async function owe(hash: string, reason: string, opts?: any): Promise<void> {
    const o = opts || {};
    const had = queue.get(hash);
    /* `refs` replaces the list outright — what the drain uses to drop the
        owners whose holds have landed. `ref` adds one, which is every other
        caller. A chunk passes neither: the account holds the file, and the
        record's hold is recorded against the manifest. */
    const refs = Array.isArray(o.refs) ? [...o.refs] : refsOf(had);
    if (typeof o.ref === 'string' && o.ref && !refs.includes(o.ref)) refs.push(o.ref);
    const row = {
      hash,
      type: o.type || (had && had.type) || '',
      refs,
      reason,
      /* A paused row stays paused through a retry that never happened — the
         drain skips it, so nothing here can quietly un-pause it. */
      state: o.state || (had && had.state === 'paused' ? 'paused'
        : (isOwed(reason) ? 'owed' : 'failed')),
      /* The row written BEFORE an attempt is not itself an attempt. Counting
         it would report every upload as having been tried twice as often as it
         was, and `tries` is what tells a person whether a stuck transfer is
         stuck or merely slow. */
      tries: (had ? Number(had.tries) || 0 : 0) + (o.attempt === false ? 0 : 1),
      at: (had && Number(had.at)) || Date.now(),
      seq: (had && Number(had.seq)) || (seq += 1),
      needs: o.needs || (had && had.needs) || undefined,
      total: o.total || (had && had.total) || undefined,
    };
    queue.set(hash, row);
    try {
      const db = await open();
      await wrap(shelf(db, QUEUE, 'readwrite').put(row, hash));
    } catch (err) { /* it is still in memory; this tab will still retry */ }
  }

  async function settle(hash: string): Promise<void> {
    queue.delete(hash);
    try {
      const db = await open();
      await wrap(shelf(db, QUEUE, 'readwrite').delete(hash));
    } catch (err) { /* the next drain finds no bytes owed and clears it */ }
  }

  /* ── draining ──────────────────────────────────────────────────────────── */

  /* One at a time. `online` and `visibilitychange` fire together often enough
     that two drains overlapping is the ordinary case, not the edge — and two
     drains means every owed blob uploaded twice, which on a metered connection
     is somebody's data plan. */
  let draining: Promise<void> | null = null;

  /** Send everything owed. Returns when the pass is done; a pass already in
      flight is joined rather than started again. */
  function mdBlobDrain(): Promise<void> {
    if (draining) return draining;
    draining = hydrated().then(pass).finally(() => { draining = null; });
    return draining;
  }

  async function pass(): Promise<void> {
    const store = await reach();
    if (!store) return;

    /* Chunks before manifests. A pass that reached a manifest first would
       find its chunks still owed, skip it, and leave it for the next pass —
       correct, but it would take one drain per level for no reason. */
    const rows = [...queue].sort((a, b) =>
      ((a[1].needs ? 1 : 0) - (b[1].needs ? 1 : 0)) || olderFirst(a[1], b[1]));

    for (const [hash, row] of rows) {
      if (row.state !== 'owed') continue;     // 'failed' is terminal, 'paused' is a person
      if (Array.isArray(row.needs) && row.needs.some((need: string) => queue.has(need))) continue;

      let bytes: Uint8Array | undefined;
      try {
        const db = await open();
        bytes = await wrap<Uint8Array | undefined>(shelf(db, BYTES, 'readonly').get(hash));
      } catch (err) { bytes = undefined; }

      /* Nothing to send. Not something the app does — but a database cleared
         by the browser, or a write that failed, both look like this, and an
         entry that can never be satisfied must not sit in the queue for the
         rest of the account's life reporting an upload in progress. */
      if (!bytes || typeof bytes.length !== 'number') { await settle(hash); continue; }

      /* ONE PUT PER OWNER. The bytes travel once per hold rather than once
         per file, which reads like waste and is not: the store answers
         `existed:true` and writes nothing the second time, and the alternative
         is a hold that was never placed for a record that believes it has one.
         A chunk owns nothing and sends once, which `[undefined]` says. */
      const refs = refsOf(row);
      const landed: string[] = [];
      let refused = '';
      for (const one of (refs.length ? refs : [undefined])) {
        let put: any = null;
        try { put = await store.blobPut(bytes, { type: row.type, ref: one }); }
        catch (err) { put = null; }
        if (put && put.ok) { if (one) landed.push(one); continue; }
        /* Stop at the first refusal. The store has just said no to these
           bytes; asking again with a different owner named is the same
           question, and on a metered connection it is somebody's data plan. */
        refused = reasonOf(put);
        break;
      }

      if (!refused) { await settle(hash); continue; }
      /* Only what is still owed stays owed, so the next pass does not re-place
         a hold that already landed. */
      await owe(hash, refused, {
        type: row.type, needs: row.needs, total: row.total,
        refs: refs.filter((r) => !landed.includes(r)),
      });
    }
  }

  /* Both events, because they answer different questions. `online` is the
     network coming back; `visibilitychange` is the person coming back, and a
     phone that slept through the reconnection fires only the second one. */
  /* The handlers hand the drain's promise back. A browser ignores what an
     event listener returns; a test can await it, and the alternative is a test
     that waits on a timer and is flaky on a slow machine for ever. */
  if (typeof g.addEventListener === 'function') {
    g.addEventListener('online', () => mdBlobDrain());
    g.addEventListener('visibilitychange', () => {
      const doc = g.document;
      if (doc && doc.visibilityState !== 'visible') return undefined;
      return mdBlobDrain();
    });
  }

  /* ── the store, whichever one it is ────────────────────────────────────── */

  const opened = new Map<string, any>();

  async function reach(): Promise<any> {
    if (typeof g.mdTransportOpen !== 'function') return null;
    if (typeof g.mdTransportName !== 'function') return null;
    const name = String(g.mdTransportName() || '');
    if (opened.has(name)) return opened.get(name);
    let transport = null;
    try {
      const got = await g.mdTransportOpen(name);
      transport = (got && got.ok) ? got.transport : null;
    } catch (err) { transport = null; }
    if (transport) opened.set(name, transport);
    return transport;
  }

  /* ── large files: many small blobs and one list of them ────────────────
     Every PUT crosses a Worker, and Cloudflare caps a request body by plan
     tier. No constant we own moves that, and R2 is not the constraint — it
     takes 5 TB objects. So the cap is removed rather than raised.

     8 MB is well under any tier's ceiling, and small chunks resume and dedupe
     better than large ones. Boundaries are FIXED-SIZE, not content-defined:
     content-defined chunking wins on files edited in the middle, and the win
     that matters here is the same file uploaded twice, which fixed boundaries
     already give in full.

     A chunk is not a special kind of thing. It is an ordinary
     content-addressed blob, hash-verified by the store exactly as any other —
     which is what makes resumability and dedupe nearly free, because the queue
     already tracks blobs and content addressing already dedupes them. */
  const MD_BLOB_CHUNK_BYTES = 8 * 1024 * 1024;

  /* The manifest announces itself in its first bytes rather than being
     recognised by its shape. A JSON sniff would misread somebody's own file
     the day its first object happened to carry the same keys, and the cost of
     that is handing back the wrong bytes under a verified hash. */
  const MANIFEST_MAGIC = 'MANDANA-CHUNKS-1\n';

  /* A file, whatever it arrived as, asked for one slice at a time. A 300 MB
     PDF is never held whole: each chunk is read, hashed and released, so the
     memory cost is the chunk size and not the file size. */
  function sliceable(source: any): { size: number; at: (a: number, b: number) => Promise<Uint8Array> } | null {
    if (source && typeof source.size === 'number' && typeof source.slice === 'function'
      && typeof source.arrayBuffer === 'function') {
      return {
        size: source.size,
        at: async (a, b) => new Uint8Array(await source.slice(a, b).arrayBuffer()),
      };
    }
    /* slice, not subarray: THE BYTES IT HASHES MUST BE THE BYTES IT KEEPS. A
       name is only a name if nothing can change what it names afterwards, and
       a view keeps the caller's buffer alive underneath it. Hashing is
       asynchronous, so a caller reading a large file into one reused scratch
       buffer — the efficient way to do it, and therefore the way it will
       eventually be written — refills that buffer while the digest is in
       flight, and the bytes cached under the first chunk's name are the
       second chunk's. Every later verification then fails, on every device,
       with nothing able to say why.

       It is taken HERE, at the boundary, because this is the one place that
       touches memory somebody else owns, and because the copy has to be made
       before the first await rather than after it. The cost is one chunk,
       bounded by MD_BLOB_CHUNK_BYTES and never by the file. */
    if (source && typeof source.length === 'number' && typeof source.slice === 'function') {
      return { size: source.length, at: async (a, b) => source.slice(a, b) };
    }
    return null;
  }

  const encoder = new TextEncoder();
  const decoder = new TextDecoder();

  function manifestBytes(size: number, type: string, chunk: number, chunks: string[]): Uint8Array {
    return encoder.encode(MANIFEST_MAGIC + JSON.stringify({ size, type, chunk, chunks }));
  }

  function readManifest(bytes: Uint8Array): any {
    if (bytes.length < MANIFEST_MAGIC.length || bytes.length > 1024 * 1024) return null;
    let head = '';
    try { head = decoder.decode(bytes.subarray(0, MANIFEST_MAGIC.length)); }
    catch (err) { return null; }
    if (head !== MANIFEST_MAGIC) return null;
    try {
      const body = JSON.parse(decoder.decode(bytes.subarray(MANIFEST_MAGIC.length)));
      if (!body || !Array.isArray(body.chunks) || typeof body.size !== 'number') return null;
      if (!body.chunks.every(isHash)) return null;
      return body;
    } catch (err) { return null; }
  }

  /* ── the door ──────────────────────────────────────────────────────────── */

  /** Keep these bytes, and get them to the store.

      `opts.ref` is the record that OWNS the bytes — never the place that
      displays them. `opts.type` is the MIME the store records beside the size.
      `opts.hash` is the caller stating what it believes the hash to be, which
      only md-backup.js's restore does; it is checked, not trusted.

      Answers `{ok:true, hash}` when the store took them, and
      `{ok:false, reason, hash}` when it did not — with the bytes cached and
      the hash pending either way, because the alternative is losing a file to
      a dropped connection. */
  async function mdBlobPut(source: unknown, opts?: any): Promise<any> {
    const options = (opts && typeof opts === 'object') ? opts : {};
    const file = sliceable(source);
    if (!file) return { ok: false, reason: 'bad_ref' };

    const chunk = (typeof options.chunk === 'number' && options.chunk > 0)
      ? options.chunk : MD_BLOB_CHUNK_BYTES;
    if (file.size > chunk) return putChunked(file, chunk, options);

    return putOne(await file.at(0, file.size), options);
  }

  /** One blob, one round trip. Every chunk goes through here too, which is the
      point: a chunk is not a special kind of thing. */
  async function putOne(body: Uint8Array, options: any): Promise<any> {
    /* `body` is already private to this call — sliceable() copied it at the
       boundary, and putChunked only ever hands on what came from there. */
    const bytes = body;
    let hash = '';
    try { hash = await g.mdTransportHash(bytes); }
    catch (err) { hash = ''; }
    if (!isHash(hash)) return { ok: false, reason: 'unreachable' };

    /* A caller that named a different hash is refused before anything is
       written anywhere. Bytes put into a store under a name they do not have
       fail every later verification, on every device, for ever — and nothing
       in the system can say why, because the store answered exactly what it
       was asked for. */
    if (options.hash !== undefined && options.hash !== hash) {
      return { ok: false, reason: 'hash_mismatch', hash };
    }

    const type = String(options.type || '');
    const owing = { type, ref: options.ref, needs: options.needs, total: options.total };
    await keep(hash, bytes, type);

    /* OWED BEFORE ATTEMPTED, never after. From the moment the bytes are in the
       cache and until something durable says they have not reached the store,
       they are indistinguishable from an ordinary landed blob — evictable by
       the trim, and the only copy in existence. A tab killed while a fetch
       hangs is not an exotic case: it is what a phone does to a page the
       moment it is backgrounded. Writing the intent afterwards records it in
       every run except the one where it was needed.

       The cost is one extra database round trip per upload. The alternative is
       a class of loss that leaves no evidence it happened. */
    await owe(hash, 'unreachable', { ...owing, attempt: false });

    const store = await reach();
    if (!store) return { ok: false, reason: 'unreachable', hash };

    let put: any = null;
    try {
      put = await store.blobPut(bytes, { type: options.type, ref: options.ref });
    } catch (err) { put = null; }

    if (!put || !put.ok) {
      const reason = reasonOf(put);
      await owe(hash, reason, owing);
      return { ok: false, reason, hash };
    }

    /* And what the store answered is checked rather than assumed. A store
       naming a hash this device did not compute is not content-addressed in
       the way everything above here depends on, and treating that as success
       would clear the queue against bytes that are not where we think they
       are — leaving another device a reference it can never resolve, which is
       precisely what story 0.9 exists to prevent. */
    if (typeof put.hash === 'string' && put.hash !== hash) {
      await owe(hash, 'hash_mismatch', owing);
      return { ok: false, reason: 'hash_mismatch', hash };
    }

    await settle(hash);
    return { ok: true, hash };
  }

  /** Chunks first, the manifest last — and the manifest names the chunks it is
      waiting on, so the drain will not send it while one of them is still
      owed. A manifest that reached the store ahead of its chunks is a
      reference to bytes that are not there: story 0.9's failure, one level
      down, and invisible to 0.9's barrier because the barrier reads documents
      and this is inside a blob. */
  async function putChunked(file: any, chunk: number, options: any): Promise<any> {
    const chunks: string[] = [];
    const owed: string[] = [];
    let reason = '';

    for (let at = 0; at < file.size; at += chunk) {
      const bytes = await file.at(at, Math.min(at + chunk, file.size));
      /* No `ref` on a chunk and no `type`: what the account holds is the file,
         and the record's hold is recorded against the manifest. A chunk shared
         by two files would otherwise carry a ref that is only half true. */
      const put = await putOne(bytes, {});
      if (!isHash(put.hash)) return { ok: false, reason: put.reason || 'unreachable' };
      chunks.push(put.hash);
      if (!put.ok) { owed.push(put.hash); reason = reason || put.reason; }
    }

    const type = String(options.type || '');
    const body = manifestBytes(file.size, type, chunk, chunks);
    const put = await putOne(body, {
      type, ref: options.ref,
      /* Only the ones still outstanding. A chunk that landed is not a
         dependency any more, and listing it would keep the manifest waiting on
         something that has already happened. */
      needs: owed.length ? [...new Set(owed)] : undefined,
      /* And how many the file is made of, which is the denominator of every
         bar drawn for it. Kept on the row rather than read out of the manifest
         bytes, so drawing a progress bar never costs a blob read. */
      total: chunks.length,
    });

    if (!isHash(put.hash)) return { ok: false, reason: put.reason || 'unreachable', chunked: true };
    return {
      ok: put.ok && owed.length === 0,
      hash: put.hash,
      chunked: true,
      chunks: chunks.length,
      ...(put.ok && owed.length === 0 ? {} : { reason: reason || put.reason || 'unreachable' }),
    };
  }

  /** The bytes named by this hash, or null.

      Null rather than a refusal object because that is what md-backup.js's
      export was written against, and because every caller's question is the
      same one: have I got the picture or not. A verified miss and an
      unreachable store are the same answer to a renderer. */
  async function mdBlobGet(hash: unknown): Promise<Uint8Array | null> {
    const bytes = await raw(hash);
    return bytes ? whole(bytes) : null;
  }

  /* The bytes stored UNDER this hash, whatever they are. A manifest comes back
     as a manifest here — which is what cancel needs, because it has to know
     which chunks the file is made of, and what mdBlobGet would hand it is the
     file with the manifest already dissolved into it. */
  async function raw(hash: unknown): Promise<Uint8Array | null> {
    if (!isHash(hash)) return null;

    try {
      const db = await open();
      const held = await wrap<Uint8Array | undefined>(shelf(db, BYTES, 'readonly').get(hash));
      if (held && typeof held.length === 'number') {
        await touch(hash);
        return held;
      }
    } catch (err) { /* fall through to the store */ }

    const store = await reach();
    if (!store) return null;

    let got: any = null;
    try { got = await store.blobGet(hash); }
    catch (err) { got = null; }
    if (!got || !got.ok || !got.bytes || typeof got.bytes.length !== 'number') return null;

    /* THE CHECK. Not cached until it passes, so a store that lied once can
       still be read correctly the next time it does not. */
    let actual = '';
    try { actual = await g.mdTransportHash(got.bytes); }
    catch (err) { actual = ''; }
    if (actual !== hash) return null;

    await keep(hash, got.bytes, String(got.type || ''));
    return got.bytes;
  }

  /* A manifest resolves into the file it names; anything else is already the
     file. Chunks come back through mdBlobGet, so each one is verified and
     cached on the way, and a chunk that is itself a manifest is refused —
     there is no legitimate nesting and allowing it is an unbounded fetch. */
  async function whole(bytes: Uint8Array): Promise<Uint8Array | null> {
    const manifest = readManifest(bytes);
    if (!manifest) return bytes;

    const out = new Uint8Array(manifest.size);
    let at = 0;
    for (const hash of manifest.chunks as string[]) {
      const part = await mdBlobGet(hash);
      if (!part) return null;
      if (readManifest(part)) return null;
      if (at + part.length > out.length) return null;
      out.set(part, at);
      at += part.length;
    }
    /* The length is the claim the manifest made. A short answer here is a
       truncated or tampered manifest, and handing it back would give the
       caller something it would write to disk and call the PDF. */
    if (at !== manifest.size) return null;
    return out;
  }

  /** A window into a file, costing the chunks the window crosses.

      SEEKING IS A VIEW; PLAYING IS AN ACQUISITION. Dragging the scrubber into
      the middle of a 300 MB film must not fetch 300 MB, and mdBlobGet does
      exactly that — it resolves the manifest by fetching every chunk it names,
      which is right for "open this PDF" and catastrophic for "show me the
      frame at 51 minutes". So this reads the manifest, works out which chunks
      the window lands in, and asks for those.

      AND IT DOES NOT CACHE WHAT IT FETCHES, which is a decision rather than an
      oversight. A film is the one thing on this device that certainly does not
      fit on it — mdBlobCacheTrim exists because that is true of far smaller
      things — so filling the cache with the chunks somebody scrubbed past
      would evict the desks and the dropped PDFs they actually came back for.
      A chunk ALREADY held is sliced from the cache, so a second seek inside
      the same eight megabytes is free; a chunk that is not held is borrowed
      and let go.

      The price of not caching is that a borrowed slice cannot be checked
      against a hash — a SHA-256 names a whole chunk, and a piece of one hashes
      to nothing in particular. That is why this is a separate verb rather than
      a faster mdBlobGet: what it hands back is good enough to draw a frame
      with and is never written down as though it had been verified.

      Byte range is [start, end), half-open, like every other range here.
      Clamped to the file rather than trusted, so a player asking past the last
      frame gets the bytes that exist instead of a buffer with invented zeroes
      on the end. */
  async function mdBlobGetRange(
    hash: unknown, start: unknown, end: unknown,
  ): Promise<Uint8Array | null> {
    if (!isHash(hash)) return null;
    if (typeof start !== 'number' || !Number.isInteger(start) || start < 0) return null;
    if (typeof end !== 'number' || !Number.isInteger(end) || end <= start) return null;

    const head = await raw(hash);
    if (!head) return null;

    /* Not a manifest: this IS the file, already in hand and already verified.
       Nothing smaller could have been fetched, so the window is a slice. */
    const manifest = readManifest(head);
    if (!manifest) return head.slice(Math.min(start, head.length), Math.min(end, head.length));

    const size: number = manifest.size;
    const span: number = manifest.chunk;
    /* A manifest without a stated chunk size cannot be indexed into, and
       guessing one would read the wrong bytes silently. Fall back to the whole
       file — slower, correct, and unreachable for anything this code wrote. */
    if (typeof span !== 'number' || !Number.isInteger(span) || span <= 0) {
      const all = await whole(head);
      return all ? all.slice(Math.min(start, all.length), Math.min(end, all.length)) : null;
    }

    const from = Math.min(start, size);
    const to = Math.min(end, size);
    if (to <= from) return new Uint8Array(0);

    const out = new Uint8Array(to - from);
    const first = Math.floor(from / span);
    const last = Math.floor((to - 1) / span);

    for (let i = first; i <= last; i += 1) {
      const chunkHash = manifest.chunks[i];
      if (!isHash(chunkHash)) return null;
      const at = i * span;                       // where this chunk starts in the file
      const a = Math.max(from, at) - at;         // …and the window inside it
      const b = Math.min(to, at + span) - at;

      const piece = await chunkRange(chunkHash, a, b);
      /* A film with a hole in it is not a film. Handing back the bytes that
         did arrive would leave a player decoding at the wrong offset with no
         way to know it, which is worse than a refusal it can retry. */
      if (!piece || piece.length !== b - a) return null;
      out.set(piece, at + a - from);
    }
    return out;
  }

  /* One chunk's window: from the cache if it is here, borrowed from the store
     if it is not. The store is asked through blobGetRange, which every
     transport answers — natively where it can (an R2 or Blossom range read),
     and by fetching whole and slicing where it cannot. Which of those happened
     is not a thing this line is allowed to know. */
  async function chunkRange(hash: string, a: number, b: number): Promise<Uint8Array | null> {
    try {
      const db = await open();
      const held = await wrap<Uint8Array | undefined>(shelf(db, BYTES, 'readonly').get(hash));
      if (held && typeof held.length === 'number') {
        await touch(hash);
        return held.slice(a, b);
      }
    } catch (err) { /* fall through to the store */ }

    const store = await reach();
    if (!store) return null;
    let got: any = null;
    try { got = await store.blobGetRange(hash, a, b); }
    catch (err) { got = null; }
    if (!got || !got.ok || !got.bytes || typeof got.bytes.length !== 'number') return null;
    return got.bytes;
  }

  /** Is it on this device? Asked by the mark that says where a record is. */
  async function mdBlobHave(hash: unknown): Promise<boolean> {
    if (!isHash(hash)) return false;
    try {
      const db = await open();
      const meta = await wrap<any>(shelf(db, META, 'readonly').get(hash));
      return !!meta;
    } catch (err) { return false; }
  }

  /** This record is letting go of these bytes.

      The ref is not optional and not decorative: after 0.7 a hold is
      `(account, hash, record)`, and a release that did not name the record
      would take the bytes away from every other record in the account holding
      them. `collected:false` is the ordinary answer — the object stays because
      somebody else still holds it, which is the whole of what content
      addressing buys. */
  async function mdBlobRelease(hash: unknown, ref: unknown): Promise<any> {
    if (!isHash(hash)) return { ok: false, reason: 'bad_ref' };
    const store = await reach();
    if (!store) return { ok: false, reason: 'unreachable' };

    let res: any = null;
    try { res = await store.blobRelease(hash, ref); }
    catch (err) { res = null; }
    if (!res || !res.ok) return { ok: false, reason: reasonOf(res) };

    await drop(hash);
    await settle(hash);
    return { ok: true, collected: !!res.collected };
  }

  /** How much this device is holding. Read from the sizes, never the bytes. */
  async function mdBlobCacheBytes(): Promise<number> {
    try {
      const db = await open();
      let total = 0;
      for (const [, meta] of await allRows(db, META)) total += Number(meta && meta.size) || 0;
      return total;
    } catch (err) { return 0; }
  }

  /** Get down to `target` bytes, oldest first — except what is still owed to
      the store, which is never evicted at any target. Answers what went, how
      much that freed, and how much could not be touched, so a caller running
      out of room is told the truth rather than left to wonder why the number
      did not move. */
  async function mdBlobCacheTrim(target: unknown): Promise<any> {
    const ceiling = (typeof target === 'number' && Number.isFinite(target) && target >= 0)
      ? target : 0;
    /* Before anything is measured, let alone dropped. Trimming against a queue
       that has not been read back yet frees exactly the bytes that had nowhere
       else to be. */
    await hydrated();
    let db: any = null;
    try { db = await open(); }
    catch (err) { return { removed: [], bytes: 0, kept: 0 }; }

    let rows: Array<[string, any]> = [];
    try { rows = await allRows(db, META); }
    catch (err) { return { removed: [], bytes: 0, kept: 0 }; }

    let total = 0;
    let kept = 0;
    const evictable: Array<{ hash: string; size: number; at: number; seq: number }> = [];
    for (const [hash, meta] of rows) {
      const size = Number(meta && meta.size) || 0;
      total += size;
      if (queue.has(hash)) { kept += size; continue; }
      evictable.push({ hash, size, at: Number(meta && meta.at) || 0, seq: Number(meta && meta.seq) || 0 });
    }
    evictable.sort(olderFirst);

    const removed: string[] = [];
    let freed = 0;
    for (const row of evictable) {
      if (total <= ceiling) break;
      await drop(row.hash);
      removed.push(row.hash);
      freed += row.size;
      total -= row.size;
    }
    return { removed, bytes: freed, kept };
  }

  /* A refusal keeps its name. An unnamed one becomes `unreachable`, which is
     the only reason that is safe to guess: it is the one the queue retries,
     and retrying something terminal is a loop, while giving up on something
     temporary is a lost file. */
  function reasonOf(res: any): string {
    const named = res && typeof res.reason === 'string' ? res.reason : '';
    if (named && Array.isArray(g.MD_BLOB_REASONS) && g.MD_BLOB_REASONS.includes(named)) {
      return named;
    }
    return named || 'unreachable';
  }

  /** Drop every memo — the open database, the opened transport, the pending
      list. For tests, and for the moment an account switch changes which
      compartment this module is looking at. */
  function mdBlobForget(): void {
    dbName = null;
    dbPromise = null;
    draining = null;
    hydrating = null;
    opened.clear();
    queue.clear();
  }

  /* ── an upload you can watch, pause and cancel ─────────────────────────
     Progress is CHUNKS DONE OVER CHUNKS TOTAL, read off the queue. Not a byte
     counter from a transport progress callback: a relay may not offer one, and
     a bar that behaves differently depending on which store is behind it has
     to be re-tested per store for ever. Chunks are a thing every store agrees
     about, because the chunking is ours. */

  function progressOf(hash: string, row: any): any {
    const total = Number(row && row.total) || 1;
    const needs: string[] = Array.isArray(row && row.needs) ? row.needs : [];
    const outstanding = needs.filter((need) => queue.has(need)).length;
    return {
      hash,
      refs: refsOf(row),
      type: (row && row.type) || '',
      state: (row && row.state) || 'owed',
      reason: (row && row.reason) || '',
      retryable: isOwed((row && row.reason) || ''),
      total,
      done: Math.max(0, Math.min(total, total - outstanding)),
    };
  }

  /** Where one file has got to. A hash nothing is owed on is `done` — which is
      every file uploaded before this device booted, and by a wide margin the
      ordinary case. A component asking about one must be able to render
      nothing rather than a bar stuck at zero. */
  async function mdBlobProgress(hash: unknown): Promise<any> {
    if (!isHash(hash)) return { hash: '', state: 'done', done: 1, total: 1, reason: '', retryable: false };
    const row = queue.get(hash);
    if (!row) return { hash, state: 'done', done: 1, total: 1, reason: '', retryable: false };
    return progressOf(hash, row);
  }

  /** Every file still in flight, one row each — never one per chunk. The
      account-wide list in 5.5 and the inline one in 1.18 are the same rows. */
  async function mdBlobTransfers(): Promise<any[]> {
    const out = [];
    for (const [hash, row] of queue) {
      if (!row || !row.total) continue;          // a bare chunk is not a transfer
      out.push(progressOf(hash, row));
    }
    out.sort((a, b) => (queue.get(a.hash).seq || 0) - (queue.get(b.hash).seq || 0));
    return out;
  }

  /* Pause reaches the chunks as well as the manifest. Pausing the manifest
     alone would leave the chunks draining merrily on, which is the opposite of
     what the button says — and is what somebody on a metered connection is
     pressing it to stop. */
  async function reState(hash: string, from: string, to: string): Promise<void> {
    const row = queue.get(hash);
    if (!row) return;
    const touched = [hash, ...(Array.isArray(row.needs) ? row.needs : [])];
    for (const one of touched) {
      const target = queue.get(one);
      if (!target || target.state !== from) continue;
      const next = { ...target, state: to };
      queue.set(one, next);
      try {
        const db = await open();
        await wrap(shelf(db, QUEUE, 'readwrite').put(next, one));
      } catch (err) { /* memory still holds it; this tab still honours it */ }
    }
  }

  /** Stop sending, and remember that across a reload — which is exactly when
      it matters, because pausing a large upload to do something else is what
      a reload usually follows. */
  async function mdBlobPause(hash: unknown): Promise<any> {
    if (!isHash(hash)) return { ok: false, reason: 'bad_ref' };
    await reState(hash, 'owed', 'paused');
    return { ok: true };
  }

  /** Start again, sending only what is missing — which 1.14 already gives. */
  async function mdBlobResume(hash: unknown): Promise<any> {
    if (!isHash(hash)) return { ok: false, reason: 'bad_ref' };
    await reState(hash, 'paused', 'owed');
    await mdBlobDrain();
    return { ok: true };
  }

  /** Give back what landed and stop naming the file.

      The chunks already stored are RELEASED, not deleted: they are
      content-addressed, so a chunk this file shares with another survives and
      only the hold goes. And because this module cannot edit a document, it
      answers with the reference the caller must take off the record — a record
      naming a file that will never arrive is the unresolvable reference 0.9
      exists to prevent, reached from the other direction. */
  async function mdBlobCancel(hash: unknown, ref: unknown): Promise<any> {
    if (!isHash(hash)) return { ok: false, reason: 'bad_ref' };
    const row = queue.get(hash);
    const chunks: string[] = Array.isArray(row && row.needs) ? row.needs : [];

    let released = 0;
    if (row && row.total) {
      const bytes = await raw(hash).catch(() => null);
      const manifest = bytes ? readManifest(bytes) : null;
      const all: string[] = manifest ? manifest.chunks : chunks;
      for (const one of all) {
        /* Only the ones the store actually took. Releasing a hold that was
           never placed is a request that can only fail or, worse, succeed
           against somebody else's. */
        if (queue.has(one)) { await settle(one); await drop(one); continue; }
        const res = await mdBlobRelease(one, ref);
        if (res.ok) released += 1;
      }
    }

    await settle(hash);
    await drop(hash);
    return { ok: true, released, unname: { hash, ref } };
  }

  /** Forget the local copy, keeping whatever the queue says about it. The
      trim's single-blob form, and the thing an account switch uses. */
  async function mdBlobEvict(hash: unknown): Promise<boolean> {
    if (!isHash(hash)) return false;
    await drop(hash);
    return true;
  }

  Object.assign(g, {
    MD_BLOB_CHUNK_BYTES,
    mdBlobPut, mdBlobGet, mdBlobGetRange, mdBlobHave, mdBlobRelease, mdBlobEvict,
    mdBlobPending, mdBlobPendingRefs, mdBlobFailed, mdBlobQueue, mdBlobQueueLoad, mdBlobDrain,
    mdBlobProgress, mdBlobTransfers, mdBlobPause, mdBlobResume, mdBlobCancel,
    mdBlobCacheBytes, mdBlobCacheTrim, mdBlobForget,
  });

  /* And the read starts now. Nothing awaits it here; everything that would be
     wrong without it awaits hydrated() at its own door. */
  hydrated();

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && typeof mod === 'object') {
    mod.exports = {
      MD_BLOB_CHUNK_BYTES,
      mdBlobPut, mdBlobGet, mdBlobGetRange, mdBlobHave, mdBlobRelease, mdBlobEvict,
      mdBlobPending, mdBlobPendingRefs, mdBlobFailed, mdBlobQueue, mdBlobQueueLoad, mdBlobDrain,
      mdBlobProgress, mdBlobTransfers, mdBlobPause, mdBlobResume, mdBlobCancel,
      mdBlobCacheBytes, mdBlobCacheTrim, mdBlobForget,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
